import { z } from 'zod';
import { centsSchema, cuidSchema, plainDateSchema } from './common.js';
/**
 * Contratos da Fase 4: contas a pagar, boletos e contas fixas.
 *
 * O sistema NAO paga nada. Ele lembra, guarda o boleto para copiar e registra
 * a baixa. Quem paga e a pessoa, no aplicativo do banco.
 */
/**
 * "Atrasada" nao esta aqui de proposito.
 *
 * Atraso e uma comparacao entre o vencimento e hoje, e nao um estado gravado.
 * Um status guardado precisaria de um job para continuar verdadeiro, e um job
 * que falhou num domingo deixa a tela dizendo "em aberto" para uma conta que
 * venceu na sexta.
 */
export const payableStatusSchema = z.enum(['OPEN', 'PAID', 'CANCELED']);
export const recurrenceFrequencySchema = z.enum(['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY']);
const shortText = z.string().trim().min(1).max(120);
const longText = z.string().trim().max(2000);
// -------------------------------------------------------------------- boleto
export const boletoKindSchema = z.enum(['BANCARIO', 'ARRECADACAO']);
/** Leitura de um boleto, sem gravar nada: a tela usa para preencher o formulario. */
export const readBoletoSchema = z.object({
    /** Linha digitavel ou codigo de barras, com ou sem pontuacao. */
    code: z.string().trim().min(30).max(80),
});
export const boletoSchema = z.object({
    kind: boletoKindSchema,
    barcode: z.string(),
    digitableLine: z.string(),
    formattedLine: z.string(),
    bankCode: z.string().nullable(),
    bankName: z.string().nullable(),
    dueDate: z.string().nullable(),
    amountCents: z.string().nullable(),
    segment: z.number().int().nullable(),
    segmentName: z.string().nullable(),
    /** Aviso quando a data teve de ser desambiguada pela virada de 2025. */
    dueDateNote: z.string().nullable(),
    amountNote: z.string().nullable(),
});
// ----------------------------------------------------------- contas a pagar
/**
 * Ganho fixo ou conta fixa.
 *
 * O padrão é saída porque a tela nasceu como "contas a pagar"; o salário e os
 * honorários recorrentes entraram depois, e é o mesmo compromisso visto pelo
 * outro lado — tem data, tem valor esperado e tem baixa.
 */
export const commitmentKindSchema = z.enum(['INCOME', 'EXPENSE']);
/**
 * O que procurar no extrato para a baixa acontecer sozinha.
 *
 * É o nome que o BANCO escreve, não o que você escreveu: "Salário" é o título
 * do compromisso, "MUNICIPIO DE PATY DO ALFERES" é o que aparece no extrato.
 * Casar por nome é muito mais firme que casar por valor — a conta de luz muda
 * todo mês, o nome da concessionária não.
 */
const counterpartyMatchSchema = z.string().trim().min(3).max(80);
export const createPayableSchema = z.object({
    description: shortText,
    dueDate: plainDateSchema,
    amountCents: centsSchema.refine((value) => value > 0n, 'Informe um valor maior que zero.'),
    kind: commitmentKindSchema.default('EXPENSE'),
    counterpartyMatch: counterpartyMatchSchema.optional(),
    categoryId: cuidSchema.optional(),
    /** Conta de onde deve sair o dinheiro. Sugestao, nao compromisso. */
    accountId: cuidSchema.optional(),
    notes: longText.optional(),
    /** Linha digitavel ou codigo de barras, quando houver boleto. */
    boletoCode: z.string().trim().max(80).optional(),
    /** Dias de antecedencia do lembrete. */
    remindDaysBefore: z.coerce.number().int().min(0).max(60).default(3),
});
export const updatePayableSchema = z.object({
    description: shortText.optional(),
    dueDate: plainDateSchema.optional(),
    amountCents: centsSchema
        .refine((value) => value > 0n, 'Informe um valor maior que zero.')
        .optional(),
    categoryId: cuidSchema.nullable().optional(),
    accountId: cuidSchema.nullable().optional(),
    notes: longText.nullable().optional(),
    boletoCode: z.string().trim().max(80).nullable().optional(),
    counterpartyMatch: counterpartyMatchSchema.nullable().optional(),
    remindDaysBefore: z.coerce.number().int().min(0).max(60).optional(),
});
/**
 * Baixa: registra que a conta foi paga e cria o lancamento correspondente.
 *
 * O valor pago pode ser diferente do previsto — juros de um dia, desconto de
 * pontualidade, conta de luz que veio mais cara. Quem manda no caixa e o
 * valor pago, e e ele que vira lancamento.
 */
export const payPayableSchema = z.object({
    accountId: cuidSchema,
    paidOn: plainDateSchema,
    amountCents: centsSchema
        .refine((value) => value > 0n, 'Informe um valor maior que zero.')
        .optional(),
    notes: longText.optional(),
});
export const payableSchema = z.object({
    id: z.string(),
    description: z.string(),
    dueDate: z.string(),
    amountCents: z.string(),
    status: payableStatusSchema,
    kind: commitmentKindSchema,
    counterpartyMatch: z.string().nullable(),
    /** Derivado do vencimento e de hoje, nunca gravado. */
    overdue: z.boolean(),
    daysUntilDue: z.number().int(),
    categoryId: z.string().nullable(),
    categoryName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    notes: z.string().nullable(),
    boletoLine: z.string().nullable(),
    boletoFormattedLine: z.string().nullable(),
    boletoBankName: z.string().nullable(),
    remindDaysBefore: z.number().int(),
    paidOn: z.string().nullable(),
    paidAmountCents: z.string().nullable(),
    paidTransactionId: z.string().nullable(),
    recurrenceId: z.string().nullable(),
    recurrenceDescription: z.string().nullable(),
    createdAt: z.string(),
});
export const payableFiltersSchema = z.object({
    status: payableStatusSchema.optional(),
    /** Mes de vencimento, AAAA-MM. */
    month: z
        .string()
        .regex(/^\d{4}-\d{2}$/, 'Informe o mes no formato AAAA-MM.')
        .optional(),
    accountId: cuidSchema.optional(),
    categoryId: cuidSchema.optional(),
    /** So o que ja venceu e continua em aberto. */
    overdueOnly: z.coerce.boolean().default(false),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
export const payableListSchema = z.object({
    payables: z.array(payableSchema),
    totals: z.object({
        openCents: z.string(),
        overdueCents: z.string(),
        paidCents: z.string(),
        count: z.number().int(),
        overdueCount: z.number().int(),
    }),
});
// ------------------------------------------------------------- contas fixas
export const createRecurrenceSchema = z
    .object({
    description: shortText,
    frequency: recurrenceFrequencySchema,
    amountCents: centsSchema.refine((value) => value > 0n, 'Informe um valor maior que zero.'),
    kind: commitmentKindSchema.default('EXPENSE'),
    counterpartyMatch: counterpartyMatchSchema.optional(),
    /** Primeiro vencimento. Os seguintes saem dele. */
    startDate: plainDateSchema,
    /** Ultimo vencimento possivel. Sem isso, a serie e indefinida. */
    endDate: plainDateSchema.optional(),
    /** Ou um numero fixo de ocorrencias, para parcelamento. */
    occurrences: z.coerce.number().int().min(1).max(600).optional(),
    categoryId: cuidSchema.optional(),
    accountId: cuidSchema.optional(),
    notes: longText.optional(),
    remindDaysBefore: z.coerce.number().int().min(0).max(60).default(3),
    active: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    if (value.endDate && value.occurrences) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['occurrences'],
            message: 'Escolha data final OU numero de ocorrencias, nao os dois.',
        });
    }
    if (value.endDate && value.endDate < value.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['endDate'],
            message: 'A data final nao pode ser antes do primeiro vencimento.',
        });
    }
});
export const updateRecurrenceSchema = z.object({
    description: shortText.optional(),
    /**
     * Muda o dia da série. Os vencimentos futuros ainda em aberto renascem no
     * dia certo; os que já receberam baixa ficam onde estão.
     */
    startDate: plainDateSchema.optional(),
    amountCents: centsSchema
        .refine((value) => value > 0n, 'Informe um valor maior que zero.')
        .optional(),
    endDate: plainDateSchema.nullable().optional(),
    categoryId: cuidSchema.nullable().optional(),
    accountId: cuidSchema.nullable().optional(),
    notes: longText.nullable().optional(),
    counterpartyMatch: counterpartyMatchSchema.nullable().optional(),
    remindDaysBefore: z.coerce.number().int().min(0).max(60).optional(),
    active: z.boolean().optional(),
});
export const recurrenceSchema = z.object({
    id: z.string(),
    description: z.string(),
    frequency: recurrenceFrequencySchema,
    amountCents: z.string(),
    kind: commitmentKindSchema,
    counterpartyMatch: z.string().nullable(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    occurrences: z.number().int().nullable(),
    categoryId: z.string().nullable(),
    categoryName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    notes: z.string().nullable(),
    remindDaysBefore: z.number().int(),
    active: z.boolean(),
    /** Quantas contas a pagar esta serie ja gerou. */
    generatedCount: z.number().int(),
    nextDueDate: z.string().nullable(),
    createdAt: z.string(),
});
export const recurrenceListSchema = z.object({ recurrences: z.array(recurrenceSchema) });
/** Gera as proximas contas de todas as series ativas. Idempotente. */
export const generatePayablesSchema = z.object({
    /** Ate quantos meses a frente. */
    monthsAhead: z.coerce.number().int().min(1).max(24).default(3),
});
export const generatePayablesResultSchema = z.object({
    created: z.number().int(),
    /** Vencimentos que ja existiam e por isso nao foram recriados. */
    skipped: z.number().int(),
});
/**
 * O que sobra do mês, depois de honrar o que já tem dono.
 *
 * É uma conta de CAIXA, e não de competência: as compras do cartão não entram
 * uma a uma, porque não é assim que elas saem da conta — sai a fatura. Somar
 * as duas coisas contaria o mesmo gasto duas vezes.
 */
export const monthCommitmentsSchema = z.object({
    month: z.string(),
    previstoEntradaCents: z.string(),
    recebidoCents: z.string(),
    aReceberCents: z.string(),
    previstoSaidaCents: z.string(),
    pagoCents: z.string(),
    aPagarCents: z.string(),
    atrasadoCents: z.string(),
    faturaCents: z.string(),
    disponivelCents: z.string(),
    sobraCents: z.string(),
});
export const reconcileResultSchema = z.object({ baixados: z.number().int() });
/**
 * O que a última leitura do VH JURIS fez.
 *
 * Não há "lançados": nenhum lançamento nasce dessa ligação. O dinheiro é
 * lançado pelo banco, e o que vem de lá é a expectativa que encontra o
 * crédito no extrato — por isso o número que interessa é `baixados`, e o que
 * sobra em `pendentes` é honorário recebido que o extrato ainda não mostrou.
 */
/**
 * O resultado de transformar um lançamento em série mensal.
 *
 * `jaExistia` não é erro: é o segundo clique numa das várias compras do mesmo
 * lugar, e a resposta certa a isso é mostrar a série que já existe.
 */
export const fixarResultSchema = z.object({
    recurrence: recurrenceSchema,
    jaExistia: z.boolean(),
    gerados: z.number().int(),
});
/** A conexão com o Asaas: ligada ou não, e desde quando. */
export const asaasStateSchema = z.object({
    conectado: z.boolean(),
    accountId: z.string().nullable(),
    startDate: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
});
export const connectAsaasSchema = z.object({
    /** A chave de API do Asaas. Vai cifrada para o banco e nunca volta à tela. */
    token: z.string().min(20).max(600),
    /** De quando em diante ler o extrato. */
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/**
 * O que a última leitura do Asaas fez.
 *
 * `pareados` é o número que protege o mês: são os repasses do Asaas que
 * encontraram o crédito correspondente no banco. Sem o par, o mesmo dinheiro
 * contaria duas vezes — uma ao cair no Asaas, outra ao chegar no banco.
 */
export const asaasResultSchema = z.object({
    conectado: z.boolean(),
    erro: z.string().nullable(),
    recebimentos: z.number().int(),
    repasses: z.number().int(),
    pareados: z.number().int(),
    baixados: z.number().int(),
    diferencaCents: z.string(),
});
export const vhJurisResultSchema = z.object({
    configurado: z.boolean(),
    erro: z.string().nullable(),
    novos: z.number().int(),
    atualizados: z.number().int(),
    removidos: z.number().int(),
    baixados: z.number().int(),
    pendentes: z.number().int(),
});
/** Resumo para o painel: o que vence logo e o que ja passou. */
export const payablesSummarySchema = z.object({
    overdueCount: z.number().int(),
    overdueCents: z.string(),
    dueSoonCount: z.number().int(),
    dueSoonCents: z.string(),
    next: z.array(z.object({
        id: z.string(),
        description: z.string(),
        dueDate: z.string(),
        amountCents: z.string(),
        overdue: z.boolean(),
    })),
});
//# sourceMappingURL=payables.js.map